'use client'

import type { Company } from '@maestro/data-layer'

import { useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

import { warmUpCustomerCache } from '@/client/api/customer'
import {
  getCompanies,
  setCompany,
} from '@maestro/commerce-blocks/client/api/companies'
import { COMPANY_SWITCHER_MODAL_KEY } from '@maestro/commerce-blocks/components/company-switcher/company-switcher.constants'
import { useCartContext } from '@maestro/commerce-blocks/context/cart.context'
import { useDebouncedCallback } from '@maestro/commerce-blocks/hooks/use-debounced-callback'
import { createLogger } from '@maestro/core/logger'
import { theme } from '@maestro/local/theme'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

const log = createLogger('maestro:commerce-blocks:company-switcher')

export const useCompanySwitcherSlideIn = (onSelected: () => void) => {
  const { isOpen } = useModals()
  const { data: session, update } = useSession()
  const { resetCart, loadUserCart } = useCartContext()
  const router = useRouter()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [selectingId, setSelectingId] = useState<string | undefined>()
  const prevIsOpen = useRef(false)
  const lastSearch = useRef<string | undefined>(undefined)
  const {
    header: {
      companySwitcher: { searchEnabled },
    },
  } = theme()

  const activeCompanyId = session?.user?.company?.id

  const fetchCompanies = useCallback(
    async (search?: string) => {
      lastSearch.current = searchEnabled ? search : undefined
      if (!prevIsOpen.current) {
        setLoading(true)
      }
      try {
        const data = await getCompanies(searchEnabled ? search : undefined)
        if (data) {
          setCompanies(data)
        }
      } catch (error) {
        log.error('Error fetching companies:', error)
      } finally {
        setLoading(false)
      }
    },
    [searchEnabled]
  )

  const debouncedFetch = useDebouncedCallback(fetchCompanies, 800)

  useEffect(() => {
    const isCompanySwitcherModalOpen = isOpen(COMPANY_SWITCHER_MODAL_KEY)

    if (isCompanySwitcherModalOpen) {
      debouncedFetch()
    }
    prevIsOpen.current = isCompanySwitcherModalOpen
  }, [isOpen, debouncedFetch])

  const selectCompany = useCallback(
    async (company: Company) => {
      setSelectingId(company.id)
      try {
        // setCompany also updates the session; the active search phrase lets
        // the server find companies that are only in the searched list
        const result = await setCompany(company.id, {
          search: lastSearch.current,
        })
        if (result.success) {
          await update()
          // switch cart to new company cart (like on login, created lazily on first add)
          resetCart()
          const customerId = session?.authContext?.userId
          if (customerId) {
            await loadUserCart(customerId)
          }
          onSelected()
          void warmUpCustomerCache().catch((error) =>
            log.error('Error warming up customer cache:', {
              id: company.id,
              error,
            })
          )
          router.refresh()
        }
      } catch (error) {
        log.error('Error setting company:', { id: company.id, error })
      } finally {
        setSelectingId(undefined)
      }
    },
    [
      loadUserCart,
      onSelected,
      resetCart,
      router,
      session?.authContext?.userId,
      update,
    ]
  )

  return {
    companies,
    areCompaniesLoading: loading,
    isSearchEnabled:
      searchEnabled && session?.authContext?.isExtendedUnitManager,
    searchCompanies: debouncedFetch,
    activeCompanyId,
    selectingId,
    selectCompany,
  }
}
